import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CustomTranslateService } from '@services/common/custom-translate.service';
import { CustomValidators } from 'app/main/helpers/custom-validators';
import { combineLatest, forkJoin, Observable, of, startWith, Subject, takeUntil } from 'rxjs';
import { NaceCode } from 'app/@types/shared/nace-code';
import naceData from '@assets/data/nace.json';
import { OnboardingDto } from 'app/@types/dto/onboarding-dto.model';
import { AdminHTTPService } from '@services/datasources/admin-http-service';
import { ActivatedRoute } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { AppRoutingService } from '@services/app-routing/app-routing.service';
import { AdminDataSyncService } from '@services/users/admin-data-sync.service';
import { Store } from '@ngxs/store';
import { buildCountryCitiesLUT, City } from 'app/@types/shared/world-cities';
import { ConfigService } from 'app/app-config.service';
import { AuthenticationService } from 'app/auth/service';
import countryData from '@assets/data/countries.json';
import { ClientAppConfig } from 'app/@types/shared/app-config';
import { UtilsService } from '@services/common/utils.service';
import { ToastService } from '@services/common/toast.service';
import { PricingPlanDto } from 'app/@types/dto/pricing-plan-dto.model';
import { FeatureService } from '@services/features/feature.service';
import { OrganizationPricingPlanDto } from 'app/@types/dto/organization-pricing-plan-dto.model';

enum PricingPlanType {
    FREE = 'bc1b9506-260b-4b3c-b680-126505a307cb',
    STARTER = 'fdb67fda-da8e-431f-b2b6-868c7000c336',
    SCAN = '3c599bd7-4536-4432-b439-026bd2edd787',
}

interface Country {
    code: string;
    name: string;
}

@Component({
    selector: 'app-organization-creation-form',
    templateUrl: './organization-creation-form.component.html',
    styleUrls: ['./organization-creation-form.component.scss'],
    providers: [ConfirmationService, MessageService],
})
export class OrganizationCreationFormComponent implements OnInit {
    public settingsForm!: FormGroup;
    public isMainAdmin: boolean;
    public countries: Country[] = [];
    private countryCitiesLUT: Map<string, City[]>;
    public filteredCities: City[] | undefined;
    public client: ClientAppConfig;
    public clientName: string;
    public availableLanguages: string[] = [];
    public isLoading = false;
    public availablePlans: PricingPlanDto[] = [];
    private defaultPlan?: PricingPlanDto =
        this.configService.getClient.name.toLowerCase() === 'dcarbonize'
            ? this.availablePlans.find((p) => p.uid === PricingPlanType.FREE)
            : this.availablePlans.find((p) => p.uid === PricingPlanType.STARTER);
    public selectedPlan?: PricingPlanDto;
    public minDate: Date = new Date();

    get organizationFormGroup(): FormGroup {
        return this.settingsForm.get('organizationGroup') as FormGroup;
    }

    get userFormGroup(): FormGroup {
        return this.settingsForm.get('userGroup') as FormGroup;
    }

    get licenseFormGroup(): FormGroup {
        return this.settingsForm.get('licenseGroup') as FormGroup;
    }

    public activities: NaceCode[];
    public onboardingInfo?: OnboardingDto = undefined;
    public selectedIndustryActivity?: NaceCode;
    private ngUnsubscribe = new Subject<void>();
    public vatPlaceholder = 'BE0123.456.789';

    constructor(
        private configService: ConfigService,
        private authenticationService: AuthenticationService,
        private translateService: CustomTranslateService,
        private featureService: FeatureService,
        private formBuilder: FormBuilder,
        private adminHttpCalls: AdminHTTPService,
        private route: ActivatedRoute,
        private confirmationService: ConfirmationService,
        private messageService: MessageService,
        private appRouting: AppRoutingService,
        private adminSignals: AdminDataSyncService,
        private cdr: ChangeDetectorRef,
        private store: Store,
        private readonly utilsService: UtilsService,
        private toastService: ToastService,
    ) {
        this.client = this.configService.getClient;

        this.initFormControls();
    }

    private initFormControls(): void {
        this.settingsForm = this.formBuilder.group({
            organizationGroup: this.formBuilder.group({
                companyLogo: new FormControl(null),
                companyName: new FormControl(null, Validators.required),
                vatNumber: new FormControl(null, [CustomValidators.optionalVatValidator]),
                country: new FormControl('', Validators.required),
                city: new FormControl(''),
                naceCode: new FormControl(null),
                industryCode: new FormControl({ value: null, disabled: true }),
                fte: new FormControl(0),
            }),
            userGroup: this.formBuilder.group({
                lastname: new FormControl(null, Validators.required),
                firstname: new FormControl(null, Validators.required),
                role: new FormControl(null),
                email: new FormControl(null, [Validators.required, CustomValidators.emailValidator]),
                phoneNumber: new FormControl(null, [CustomValidators.phoneNumberValidator]),
                defaultLocale: new FormControl(this.configService.getConfig.defaultLocale),
            }),
            licenseGroup: this.formBuilder.group({
                license: new FormControl(null, Validators.required),
                users: new FormControl(1, Validators.min(1)),
                entities: new FormControl(1, Validators.min(1)),
                period: new FormControl(false),
                validity: new FormControl(new Date(), Validators.required),
            }),
        });

        this.initializeFormValueSubscriptions();
    }

    ngOnInit(): void {
        this.clientName = this.client.name;
        this.availableLanguages = this.configService.getConfig.supportedLanguages;
        this.isMainAdmin = this.route.snapshot.data['isMainAdmin'];

        // Combine all initialization observables
        combineLatest([
            this.translateService.get('nace'),
            this.translateService.get('components.settingsForm.industry'),
            this.adminHttpCalls.getPricingPlans(),
            this.translateService.onLangChange.pipe(startWith(null)),
        ])
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(([naceTranslation, industrySectorTranslation, plans, _]) => {
                // Initialize activities
                this.activities = naceData.map((item) => {
                    return {
                        ...item,
                        label: naceTranslation[item.translationKey],
                        industrySectorLabel: industrySectorTranslation[item.industrySectorLabel],
                    };
                });

                // Initialize pricing plans
                this.availablePlans = plans.sort((a, b) => {
                    // Mettre FREE ou STARTER en premier
                    if (
                        (a.uid === PricingPlanType.FREE || a.uid === PricingPlanType.STARTER) &&
                        !(b.uid === PricingPlanType.FREE || b.uid === PricingPlanType.STARTER)
                    ) {
                        return -1;
                    }
                    if (
                        !(a.uid === PricingPlanType.FREE || a.uid === PricingPlanType.STARTER) &&
                        (b.uid === PricingPlanType.FREE || b.uid === PricingPlanType.STARTER)
                    ) {
                        return 1;
                    }
                    // Mettre SCAN après
                    if (a.uid === PricingPlanType.SCAN && b.uid !== PricingPlanType.SCAN) {
                        return 1;
                    }
                    if (a.uid !== PricingPlanType.SCAN && b.uid === PricingPlanType.SCAN) {
                        return -1;
                    }
                    return 0;
                });
                this.defaultPlan =
                    this.configService.getClient.name.toLowerCase() === 'dcarbonize'
                        ? plans.find((p) => p.uid === PricingPlanType.FREE)
                        : plans.find((p) => p.uid === PricingPlanType.STARTER);

                // Set default license after plans are loaded
                const licenseControl = this.licenseFormGroup.get('license');
                if (licenseControl && !licenseControl.value) {
                    licenseControl.setValue(this.defaultPlan?.uid);
                }

                // Initialize countries
                this.countries = [];
                this.translateService.get('countries').subscribe((countries) => {
                    Object.entries(countries).forEach(([code, name]) => {
                        this.countries.push({ code: code, name: name });
                    });
                    this.countries.sort((a, b) => a.name.localeCompare(b.name));
                });

                // Initialize form values if needed
                this.route.queryParams.pipe(takeUntil(this.ngUnsubscribe)).subscribe((params) => {
                    this.countryCitiesLUT = buildCountryCitiesLUT();
                    this.settingsForm.reset();
                    if (params && params.organizationId) {
                        this.settingsForm.disable();
                        this.adminHttpCalls
                            .getOnboardingInfo(params.organizationId)
                            .pipe(takeUntil(this.ngUnsubscribe))
                            .subscribe({
                                next: (onboardingDto) => {
                                    this.onboardingInfo = onboardingDto;
                                    this.adminSignals.updateTitleSubject.next(this.onboardingInfo.name);
                                    this.settingsForm.enable();
                                    this.initFormValues();
                                },
                                error: () => {
                                    this.settingsForm.enable();
                                    //TODO:handleError
                                },
                            });
                    } else if (params && !params.organizationId) {
                        this.adminSignals.updateTitleSubject.next('new_organization');
                        this.settingsForm.enable();
                        this.organizationFormGroup.get('city')?.disable();
                        this.onboardingInfo = undefined;
                        this.initFormValues();
                    }
                });
            });
    }

    private initializeFormValueSubscriptions(): void {
        this.organizationFormGroup.get('naceCode')!.valueChanges.subscribe((selectedNace) => {
            [this.selectedIndustryActivity] = this.activities.filter((code) => code.id === selectedNace);
            if (this.selectedIndustryActivity) {
                this.organizationFormGroup
                    .get('industryCode')!
                    .setValue(this.selectedIndustryActivity.industrySectorId);
            } else {
                this.organizationFormGroup.get('industryCode')!.reset();
            }
        });

        this.userFormGroup.get('email')?.valueChanges.subscribe((value) => {
            if (value) {
                const lowerCaseValue = value.toLowerCase();
                if (value !== lowerCaseValue) {
                    this.userFormGroup.get('email')?.setValue(lowerCaseValue, { emitEvent: false });
                }
            }
        });

        this.licenseFormGroup
            .get('license')
            ?.valueChanges.pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((licenseId: string) => {
                if (licenseId) this.handleLicenseTypeChange(licenseId);
            });
    }

    onCountryChange(event: any): void {
        const country = countryData.find((country) => country.id === event.value);
        const vatNumberControl = this.organizationFormGroup.get('vatNumber');

        vatNumberControl?.setValue('');
        vatNumberControl?.clearValidators();

        this.organizationFormGroup.get('country')?.setValue(event.value);
        this.vatPlaceholder = 'BE0123.456.789';

        if (country && country?.regexVaT) {
            vatNumberControl?.addValidators(
                CustomValidators.regexValidator(new RegExp(country.regexVaT), country.vatErrorMessage),
            );
            this.vatPlaceholder = country.placeholderVAT;
        }

        vatNumberControl?.markAsPristine();
        vatNumberControl?.setErrors(null);
    }

    private handleLicenseTypeChange(id: string): void {
        const plan = this.availablePlans.find((p) => p.uid === id);
        if (!plan) return;

        this.selectedPlan = plan;
        const usersControl = this.licenseFormGroup.get('users');
        const entitiesControl = this.licenseFormGroup.get('entities');
        const periodControl = this.licenseFormGroup.get('period');
        const validityControl = this.licenseFormGroup.get('validity');

        if (plan.uid === PricingPlanType.FREE || plan.uid === PricingPlanType.STARTER) {
            usersControl?.setValue(plan.doc.max_users);
            entitiesControl?.setValue(plan.doc.max_entities);
            periodControl?.setValue(plan.doc.multi_period);
            validityControl?.setValue(
                new Date(
                    this.onboardingInfo?.licenseValidity ??
                        Date.now() + plan.doc.default_validity! * 24 * 60 * 60 * 1000,
                ),
            );
            usersControl?.disable();
            entitiesControl?.disable();
        } else {
            usersControl?.setValue(
                this.onboardingInfo &&
                    this.onboardingInfo.licenseDoc &&
                    this.onboardingInfo.licenseDoc.max_users > plan.doc.max_users
                    ? this.onboardingInfo?.licenseDoc?.max_users ?? plan.doc.max_users
                    : plan.doc.max_users,
            );
            entitiesControl?.setValue(
                this.onboardingInfo &&
                    this.onboardingInfo.licenseDoc &&
                    this.onboardingInfo.licenseDoc.max_entities > plan.doc.max_entities
                    ? this.onboardingInfo?.licenseDoc?.max_entities ?? plan.doc.max_entities
                    : plan.doc.max_entities,
            );
            periodControl?.setValue(plan.doc.multi_period);
            validityControl?.setValue(
                new Date(
                    this.onboardingInfo?.licenseValidity ??
                        Date.now() + plan.doc.default_validity! * 24 * 60 * 60 * 1000,
                ),
            );
            usersControl?.enable();
            entitiesControl?.enable();
        }
    }

    initFormValues(): void {
        const organizationGroup = this.organizationFormGroup;
        const userGroup = this.userFormGroup;
        this.isLoading = true;
        const licenseGroup = this.licenseFormGroup;

        organizationGroup.get('companyLogo')!.setValue(this.onboardingInfo?.avatar);
        organizationGroup.get('companyName')!.setValue(this.onboardingInfo?.name);
        organizationGroup.get('vatNumber')!.setValue(this.onboardingInfo?.tva);
        organizationGroup.get('country')!.setValue(this.onboardingInfo?.country);
        this.filteredCities = this.countryCitiesLUT.get(this.onboardingInfo?.country!);
        organizationGroup.get('city')?.setValue(parseInt(this.onboardingInfo?.cityCode!));
        organizationGroup.get('naceCode')!.setValue(this.onboardingInfo?.nace);
        organizationGroup.get('industryCode')!.setValue(this.onboardingInfo?.industryCode);

        userGroup.get('lastname')!.setValue(this.onboardingInfo?.lastName);
        userGroup.get('firstname')!.setValue(this.onboardingInfo?.firstName);
        userGroup.get('role')!.setValue(this.onboardingInfo?.role);
        userGroup.get('email')!.setValue(this.onboardingInfo?.email);
        let phoneNumber: string | null = null;
        if (this.onboardingInfo?.phone_number !== undefined && this.onboardingInfo?.phone_number !== '+3200000000000')
            phoneNumber = this.onboardingInfo?.phone_number;
        userGroup.get('phoneNumber')!.setValue(phoneNumber);
        userGroup
            .get('defaultLocale')!
            .setValue(this.onboardingInfo?.locale ?? this.configService.getConfig.defaultLocale);

        licenseGroup.get('license')!.setValue(this.onboardingInfo?.licenseId ?? this.defaultPlan?.uid);
        licenseGroup
            .get('users')!
            .setValue(this.onboardingInfo?.licenseDoc?.max_users ?? this.defaultPlan?.doc.max_users);
        licenseGroup
            .get('entities')!
            .setValue(this.onboardingInfo?.licenseDoc?.max_entities ?? this.defaultPlan?.doc.max_entities);
        licenseGroup
            .get('period')!
            .setValue(this.onboardingInfo?.licenseDoc?.multi_period ?? this.defaultPlan?.doc.multi_period);
        licenseGroup
            .get('validity')!
            .setValue(
                this.onboardingInfo?.licenseValidity ??
                    new Date(Date.now() + this.defaultPlan?.doc.default_validity! * 24 * 60 * 60 * 1000),
            );

        organizationGroup.markAsPristine();
        userGroup.markAsPristine();
        licenseGroup.markAsPristine();
        this.onboardingInfo !== undefined ? userGroup.disable() : userGroup.enable();
        this.isLoading = false;
        this.userIsGlobalAdmin() ? licenseGroup.enable() : licenseGroup.disable();
    }

    filterCities() {
        const country = this.organizationFormGroup.get('country')?.value;
        if (country) {
            this.filteredCities = this.countryCitiesLUT.get(country);
            this.organizationFormGroup.get('city')?.enable();
        }
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    public customFormValidity(): boolean {
        const isOrganizationGroupValid = this.organizationFormGroup.valid && !this.organizationFormGroup.pristine;
        const isLicenseGroupValid = this.licenseFormGroup.valid && !this.licenseFormGroup.pristine;
        const isUserGroupValid = this.userFormGroup.valid || this.userFormGroup.disabled;
        const isLicenseGroupEnabled = this.licenseFormGroup.valid || this.licenseFormGroup.disabled;

        return (isOrganizationGroupValid || isLicenseGroupValid) && isUserGroupValid && isLicenseGroupEnabled;
    }

    onSubmit() {
        this.isLoading = true;
        const organizationGroup = this.organizationFormGroup;
        const userGroup = this.userFormGroup;
        const licenseGroup = this.licenseFormGroup;

        if (!this.onboardingInfo) {
            this.adminHttpCalls
                .createOrganizationUser(
                    {
                        avatar: organizationGroup.get('companyLogo')!.value,
                        name: organizationGroup.get('companyName')!.value,
                        tva: organizationGroup.get('vatNumber')!.value,
                        country: organizationGroup.get('country')!.value,
                        cityCode: organizationGroup.get('city')!.value,
                        nace: organizationGroup.get('naceCode')!.value,
                        industryCode: organizationGroup.get('industryCode')!.value,
                        firstName: userGroup.get('firstname')!.value,
                        lastName: userGroup.get('lastname')!.value,
                        email: userGroup.get('email')!.value,
                        phone_number: userGroup.get('phoneNumber')!.value.replace(/[^0-9+]/g, ''),
                        locale: userGroup.get('defaultLocale')!.value,
                        role: userGroup.get('role')!.value,
                        licenseId: licenseGroup.get('license')!.value,
                        licenseDoc: {
                            max_entities: licenseGroup.get('entities')!.value,
                            max_users: licenseGroup.get('users')!.value,
                            multi_period: licenseGroup.get('period')!.value,
                        },
                        licenseValidity: licenseGroup.get('validity')!.value,
                    },
                    this.clientName,
                    // this.isMainAdmin ? this.adminSignals.currentSelectedDedicatedAdminId.getValue() : undefined,
                    this.adminSignals.currentSelectedDedicatedAdminId.getValue(),
                )
                .subscribe({
                    next: (result) => {
                        this.onboardingInfo = result;
                        this.adminSignals.currentSelectedOrgId.next(result.uid ?? undefined);
                        this.adminSignals.udpateListSubject.next(true);

                        this.appRouting.navigateToOganisationForm();
                        this.adminSignals.updateTitleSubject.next(this.onboardingInfo.name);
                        this.settingsForm.markAsPristine();
                        this.toastService.showSuccess({
                            details: this.translateService.instant('admin.dedicatedAdmin.organization_created'),
                        });
                        this.isLoading = false;
                    },
                    error: () => {
                        this.isLoading = false;
                        this.toastService.showError({
                            details: this.translateService.instant('admin.dedicatedAdmin.organization_created_fail'),
                        });
                    },
                });
        } else {
            const organizationUpdate$ = this.adminHttpCalls.updateOrganization(this.onboardingInfo.uid!, {
                uid: this.onboardingInfo.uid,
                avatar: organizationGroup.get('companyLogo')!.value,
                name: organizationGroup.get('companyName')!.value,
                tva: organizationGroup.get('vatNumber')!.value,
                country: organizationGroup.get('country')!.value,
                cityCode: organizationGroup.get('city')!.value,
                nace: organizationGroup.get('naceCode')!.value,
                industryCode: organizationGroup.get('industryCode')!.value,
                isActive: true,
                isOnboarded: this.onboardingInfo.isOnboarded ?? false,
            });
            let pricingPlanUpdate$ = of(null) as Observable<OrganizationPricingPlanDto | null>;
            if (this.userIsGlobalAdmin()) {
                pricingPlanUpdate$ = this.adminHttpCalls.updateOrganizationPricingPlan(this.onboardingInfo.uid!, {
                    organizationId: this.onboardingInfo.uid!,
                    pricingPlanId: licenseGroup.get('license')!.value,
                    licenseDoc: {
                        max_entities: licenseGroup.get('entities')!.value,
                        max_users: licenseGroup.get('users')!.value,
                        multi_period: licenseGroup.get('period')!.value,
                    },
                    validUntil: this.toNeutralDate(licenseGroup.get('validity')!.value),
                });
            }

            // Execute both updates in parallel
            forkJoin([organizationUpdate$, pricingPlanUpdate$]).subscribe({
                next: ([result, planResult]) => {
                    this.onboardingInfo!.name = result.name;
                    this.onboardingInfo!.tva = result.tva;
                    this.onboardingInfo!.nace = result.nace;
                    this.onboardingInfo!.industryCode = result.industryCode;
                    this.onboardingInfo!.requestedSolutions = result.requestedSolutions;

                    this.adminSignals.udpateListSubject.next(true);
                    this.adminSignals.updateTitleSubject.next(this.onboardingInfo!.name);
                    this.settingsForm.markAsPristine();
                    this.isLoading = false;
                    this.toastService.showSuccess({
                        details: this.translateService.instant('admin.dedicatedAdmin.organization_edited'),
                    });
                },
                error: () => this.handleOrganizationUpdateFail(),
            });
        }
    }

    delete(): void {
        combineLatest([
            this.translateService.get('components.settingsForm.confirmDeletion', {
                name: this.onboardingInfo!.name,
            }),
            this.translateService.get('common.confirm_title'),
            this.translateService.get('common.confirm_button'),
            this.translateService.get('common.cancel_button'),
        ]).subscribe((translations) => {
            this.confirmationService.confirm({
                message: this.utilsService.handleTranslation(translations[0]),
                header: this.utilsService.handleTranslation(translations[1]),
                acceptLabel: this.utilsService.handleTranslation(translations[2]),
                rejectLabel: this.utilsService.handleTranslation(translations[3]),
                rejectButtonStyleClass: 'dc--btn-tertiary',
                icon: 'pi pi-exclamation-triangle',
                accept: () => {
                    this.messageService.add({ severity: 'info', summary: 'Confirmed', detail: 'You have accepted' });
                    if (this.onboardingInfo?.uid) {
                        this.adminHttpCalls
                            .deleteOrganization(this.onboardingInfo.uid)
                            .pipe(takeUntil(this.ngUnsubscribe))
                            .subscribe({
                                next: () => {
                                    this.adminSignals.udpateListSubject.next(true);
                                    // this.isMainAdmin
                                    //     ? this.appRouting.navigateToOganisationAdminForm()
                                    //     : this.appRouting.navigateToOganisationForm();
                                    this.appRouting.navigateToOganisationAdminForm();
                                },
                                error: () => this.handleOrganizationUpdateFail(),
                            });
                    }
                },
                reject: (type) => {},
            });
        });
    }

    handleOrganizationUpdateFail() {
        this.isLoading = false;
        this.toastService.showError({
            details: this.translateService.instant('admin.dedicatedAdmin.organization_edited_fail'),
        });
        this.adminSignals.currentSelectedOrgId.next(undefined);
        this.adminSignals.udpateListSubject.next(true);
        // this.isMainAdmin
        //     ? this.appRouting.navigateToOganisationAdminForm()
        //     : this.appRouting.navigateToOganisationForm();
        this.appRouting.navigateToOganisationAdminForm();
    }
    onLabelClick(id: string) {
        const primeInput = document.getElementById(id);
        if (primeInput) {
            const input = this.getInputElement(primeInput);
            input?.focus();
        }
    }
    getInputElement(primeInput: HTMLElement): HTMLElement | null {
        return (
            primeInput.querySelector('p-dropdown') ||
            primeInput.querySelector('p-radiobutton') ||
            primeInput.querySelector('input')
        );
    }

    public userIsGlobalAdmin(): boolean {
        return (
            this.featureService.hasUiFeatures(['admin_admin'], false) ||
            this.featureService.hasUiFeatures(['admin_admin'], true)
        );
    }

    isLicenseCustomizable(): boolean {
        return this.selectedPlan?.doc.customizable ?? false;
    }

    /**
     * Copies the UID of the onboarding organization to the clipboard.
     *
     * This method checks if the `onboardingInfo` object and its `uid` property are defined. If so,
     * it attempts to copy the `uid` to the user's clipboard using the Clipboard API.
     * It also handles success and error scenarios, logging appropriate messages to the console.
     */
    copyUid(): void {
        // Ensure `onboardingInfo` and `uid` are defined before attempting to copy
        if (this.onboardingInfo?.uid) {
            // Use the Clipboard API to write the UID to the clipboard
            navigator.clipboard
                .writeText(this.onboardingInfo.uid) // Copies the UID to the clipboard
                .then(() => {
                    // If the copy operation is successful
                    console.log('UID copied!'); // Log success message
                })
                .catch((err) => {
                    // If the copy operation fails (e.g., due to permission issues)
                    console.error('Error copying UID: ', err); // Log error message
                });
        }
    }

    /**
     * Converts a Date object to a neutral date string in ISO format.
     * This method removes timezone information by creating a UTC date with only year, month, and day,
     * then converts it to ISO string format (YYYY-MM-DDTHH:mm:ss.sssZ).
     *
     * @param date - The Date object to convert
     * @returns A neutral date string in ISO format without timezone bias
     */
    toNeutralDate(date: Date): string {
        return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())).toISOString();
    }
}
